import type { VariablesOf } from '@graphql-typed-document-node/core'
import {
  CREATE_USER_CUSTOM_LIST,
  UPDATE_USER_CUSTOM_LIST,
} from '@/queries/customLists.ts'
import {
  UserCustomListModeType,
  UserCustomListStatusFilterType,
  UserCustomListTargetTypeType,
} from '../../__generated__/graphql'
import type { SyncableAction } from '@/stores/sync/createSyncEngine.ts'
import type { CustomListDeltaRow } from '@/stores/user-sync'

// The list-config fields the create/edit form owns. Server ids stay out of it —
// the action references the list by its stable client urlName.
export type ListCrudMeta = {
  name: string
  description: string
  targetType: UserCustomListTargetTypeType
  isPublic: boolean
  isCustomTag: boolean
  customTagName: string
  removeWhenCollected: boolean
  removeWhenRead: boolean
  showTotalNumber: boolean
  showTotalValue: boolean
}

// urlName = the stable client-facing identity (also the route key). serverId is
// filled once a create syncs; update/delete resolve it live at drain time.
export type ListCrudAction = SyncableAction & {
  op: 'create' | 'update' | 'delete'
  urlName: string
  serverId: string | null
  meta: ListCrudMeta
}

// Delete carries no metadata (performSync ignores it); a shared empty value keeps
// the action shape uniform.
export const EMPTY_LIST_META: ListCrudMeta = {
  name: '',
  description: '',
  targetType: UserCustomListTargetTypeType.ReleaseEntry,
  isPublic: false,
  isCustomTag: false,
  customTagName: '',
  removeWhenCollected: false,
  removeWhenRead: false,
  showTotalNumber: false,
  showTotalValue: false,
}

export function metaToCreateData(
  meta: ListCrudMeta,
  urlName: string,
): VariablesOf<typeof CREATE_USER_CUSTOM_LIST>['data'] {
  return {
    urlName,
    name: meta.name,
    description: meta.description,
    targetType: meta.targetType,
    isPublic: meta.isPublic,
    isCustomTag: meta.isCustomTag,
    customTagName: meta.customTagName,
    removeWhenCollected: meta.removeWhenCollected,
    removeWhenRead: meta.removeWhenRead,
    showTotalNumber: meta.showTotalNumber,
    showTotalValue: meta.showTotalValue,
    mode: UserCustomListModeType.Curated,
  }
}

export function metaToUpdateData(
  meta: ListCrudMeta,
): VariablesOf<typeof UPDATE_USER_CUSTOM_LIST>['data'] {
  return {
    name: meta.name,
    description: meta.description,
    targetType: meta.targetType,
    isPublic: meta.isPublic,
    isCustomTag: meta.isCustomTag,
    customTagName: meta.customTagName,
    removeWhenCollected: meta.removeWhenCollected,
    removeWhenRead: meta.removeWhenRead,
    showTotalNumber: meta.showTotalNumber,
    showTotalValue: meta.showTotalValue,
  }
}

// Build the delta-row an offline-created list shows as until its real row arrives.
// id = urlName (placeholder route key); server-op call sites see no real row yet.
function syntheticRow(action: ListCrudAction): CustomListDeltaRow {
  return {
    __typename: 'UserCustomList',
    id: action.urlName,
    urlName: action.urlName,
    name: action.meta.name,
    description: action.meta.description,
    targetType: action.meta.targetType,
    mode: UserCustomListModeType.Curated,
    isPublic: action.meta.isPublic,
    isCustomTag: action.meta.isCustomTag,
    customTagName: action.meta.customTagName,
    customIcon: null,
    removeWhenCollected: action.meta.removeWhenCollected,
    removeWhenRead: action.meta.removeWhenRead,
    showTotalNumber: action.meta.showTotalNumber,
    showTotalValue: action.meta.showTotalValue,
    statusFilter: UserCustomListStatusFilterType.Collection,
    filter: {},
    sort: [],
    order: 0,
  }
}

function applyMeta(row: CustomListDeltaRow, meta: ListCrudMeta): CustomListDeltaRow {
  return {
    ...row,
    name: meta.name,
    description: meta.description,
    targetType: meta.targetType,
    isPublic: meta.isPublic,
    isCustomTag: meta.isCustomTag,
    customTagName: meta.customTagName,
    removeWhenCollected: meta.removeWhenCollected,
    removeWhenRead: meta.removeWhenRead,
    showTotalNumber: meta.showTotalNumber,
    showTotalValue: meta.showTotalValue,
  }
}

// Lay pending create/update/delete onto the delta base, reconciled by urlName so a
// synced list is never duplicated and a local-only one never vanishes.
// A row's stable key. urlName is it, but rows created before the urlName backfill
// have none — fall back to the row id, which is exactly what the action carries in
// that case (ListForm resolves urlName ?? listId). Note: the add-column migration
// defaulted legacy rows to '' (empty string, NOT null), so guard against a
// falsy-but-non-null urlName too or every legacy list collides on the '' key.
export function listRowKey(row: CustomListDeltaRow): string {
  return row.urlName && row.urlName.length > 0 ? row.urlName : row.id
}

export function buildOptimisticListRows(
  base: CustomListDeltaRow[],
  actions: ListCrudAction[],
): CustomListDeltaRow[] {
  const pending = actions.filter((action) => !action.synced)
  const deleted = new Set(pending.filter((a) => a.op === 'delete').map((a) => a.urlName))
  const updateByKey = new Map(pending.filter((a) => a.op === 'update').map((a) => [a.urlName, a.meta]))
  const baseKeys = new Set(base.map(listRowKey))

  const merged = base
    .filter((row) => !deleted.has(listRowKey(row)))
    .map((row) => {
      const meta = updateByKey.get(listRowKey(row))
      return meta ? applyMeta(row, meta) : row
    })

  const optimisticCreates = pending
    .filter((a) => a.op === 'create' && !deleted.has(a.urlName) && !baseKeys.has(a.urlName))
    .map(syntheticRow)

  return [...merged, ...optimisticCreates]
}

// The server row id for a list reference, which may be a urlName (client-created
// list) or already a server id (existing list): the synced base row matched by
// either, else a create action that has already been answered.
export function resolveListServerIdFrom(
  urlNameOrId: string,
  base: CustomListDeltaRow[],
  actions: ListCrudAction[],
): string | null {
  const byUrlName = base.find((row) => row.urlName === urlNameOrId)
  if (byUrlName) return byUrlName.id
  const byId = base.find((row) => row.id === urlNameOrId)
  if (byId) return byId.id
  const createAction = actions.find((a) => a.op === 'create' && a.urlName === urlNameOrId)
  return createAction?.serverId ?? null
}
